import { ChangeCashMode } from '../model';
import type { FC } from 'react';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormatting } from '@pflegenavi/frontend/localization';
import { Divider, Stack } from '@mui/material';
import { Typography } from '@pflegenavi/web-components';

interface CustomBankContentProps {
  totalCashBalance: number;
  mode: ChangeCashMode;
  cashChange: number;
  cashListName?: string;
}

export const CustomCashContent: FC<CustomBankContentProps> = ({
  totalCashBalance,
  cashChange,
  mode,
  cashListName,
}) => {
  const { t } = useTranslation();
  const { fCurrencyCents } = useFormatting();

  const isDeposit = mode === ChangeCashMode.Deposit;

  const changeFactor = useMemo(() => {
    if (isDeposit) {
      return 1;
    }
    return -1;
  }, [isDeposit]);

  const newBalance = totalCashBalance + changeFactor * (cashChange ?? 0);

  return (
    <Stack sx={{ gap: 0.5 }}>
      <Stack>
        <Stack
          direction="row"
          spacing={5}
          sx={{
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 700,
            }}
          >
            {cashListName
              ? t('cashManagement.account.current-balance', {
                  name: cashListName,
                })
              : t('cashManagement.current-cash-account-balance')}
          </Typography>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 700,
            }}
          >
            {fCurrencyCents(totalCashBalance)}
          </Typography>
        </Stack>
        <Stack
          direction="row"
          spacing={5}
          sx={{
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="subtitle2">
            {t(`cashManagement.${isDeposit ? 'deposit' : 'withdrawal'}-amount`)}
          </Typography>
          <Typography
            variant="subtitle2"
            color={isDeposit ? 'success.main' : 'error.main'}
          >
            {cashChange === 0 || cashChange === undefined
              ? '--'
              : fCurrencyCents(changeFactor * cashChange)}
          </Typography>
        </Stack>
      </Stack>
      <Divider />
      <Stack
        direction="row"
        spacing={5}
        sx={{
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 700,
          }}
        >
          {cashListName
            ? t('cashManagement.account.new-balance', { name: cashListName })
            : t('cashManagement.new-cash-account-balance')}
        </Typography>
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 700,
          }}
        >
          {fCurrencyCents(newBalance)}
        </Typography>
      </Stack>
    </Stack>
  );
};
